import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Truck, ChevronRight, Loader2, Ship } from "lucide-react";
import api from "@/lib/api";

const STATUS_LABELS = {
  created: { label: "Enregistrée", color: "bg-black/60" },
  picked_up: { label: "Récupérée à Yiwu", color: "bg-blue-500" },
  in_transit_china: { label: "Transit en Chine", color: "bg-blue-500" },
  in_transit: { label: "Transit international", color: "bg-indigo-500" },
  arrived_port: { label: "Arrivée port/aéroport", color: "bg-purple-500" },
  customs_clearance: { label: "Dédouanement", color: "bg-orange-500" },
  out_for_delivery: { label: "En livraison", color: "bg-yellow-500" },
  delivered: { label: "Livrée", color: "bg-green-500" },
  exception: { label: "Incident", color: "bg-red-500" },
};

const SERVICE_LABELS = {
  express: { label: "Fret express aérien", icon: Truck },
  normal: { label: "Fret normal aérien", icon: Truck },
  maritime: { label: "Fret maritime", icon: Ship },
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function MyShipments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/shipments/mine")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mt-12" data-testid="my-shipments">
      <div className="flex items-center gap-3 mb-4">
        <Package className="text-[var(--accent)]" />
        <h2 className="font-display text-2xl font-black">Mes expéditions</h2>
        <span className="ml-2 text-sm text-[var(--ink-soft)]">{items.length}</span>
      </div>

      {loading ? (
        <div className="card-flat p-10 flex items-center justify-center">
          <Loader2 className="animate-spin text-[var(--ink-soft)]" />
        </div>
      ) : items.length === 0 ? (
        <div className="card-flat p-10 text-center">
          <Package className="mx-auto text-[var(--ink-soft)]" size={32} />
          <p className="text-[var(--ink-soft)] mt-3">Vous n'avez pas encore d'expédition enregistrée.</p>
          <p className="text-xs text-[var(--ink-soft)] mt-2">
            Dès qu'une expédition est créée avec votre email, elle apparaîtra ici.
          </p>
          <Link to="/suivi" className="btn-outline mt-6 inline-flex text-xs !py-2 !px-3" data-testid="my-shipments-track-any">
            Suivre un colis par numéro
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((s) => {
            const st = STATUS_LABELS[s.current_status] || { label: s.current_status || "—", color: "bg-black/60" };
            const svc = SERVICE_LABELS[s.service_type] || { label: s.service_type, icon: Truck };
            const Icon = svc.icon;
            const isOpen = expanded === s.id;
            return (
              <li key={s.id} className="card-flat overflow-hidden" data-testid={`my-shipment-${s.tracking_number}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : s.id)}
                  className="w-full text-left p-5 flex items-center gap-4 hover:bg-[var(--bg-soft)] transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-[var(--bg-soft)] flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-[var(--ink-soft)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-mono font-bold text-sm">{s.tracking_number}</p>
                      <span className={`text-[10px] font-bold uppercase tracking-widest text-white px-2 py-0.5 rounded-full ${st.color}`}>
                        {st.label}
                      </span>
                      {s.amount_due != null && (
                        <span className="text-[10px] font-bold text-[var(--accent)] bg-[var(--accent)]/10 px-2 py-0.5 rounded-full">
                          💰 {Math.round(Number(s.amount_due)).toLocaleString("fr-FR")} {s.amount_currency || "FCFA"}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--ink-soft)] mt-1 truncate">
                      {svc.label} · {s.origin} → {s.destination}
                    </p>
                  </div>
                  <ChevronRight size={18} className={`transition-transform text-[var(--ink-soft)] ${isOpen ? "rotate-90" : ""}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-black/5 p-5 space-y-4 bg-[var(--bg-soft)]/40">
                    {s.amount_due != null && (
                      <div className="rounded-lg bg-gradient-to-r from-[var(--accent)] to-[#D62828] p-4 text-white flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest opacity-90">À payer à l'arrivée</p>
                          <p className="font-display text-2xl font-black mt-0.5">
                            {Math.round(Number(s.amount_due)).toLocaleString("fr-FR")} <span className="text-xs font-bold">{s.amount_currency || "FCFA"}</span>
                          </p>
                        </div>
                        <span className="text-[10px] uppercase tracking-widest opacity-90">Règlement à la livraison</span>
                      </div>
                    )}

                    {(s.photos || []).length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)] mb-2">Photos de la marchandise</p>
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                          {s.photos.map((url, i) => (
                            <a key={url + i} href={url} target="_blank" rel="noopener noreferrer" className="aspect-square rounded overflow-hidden bg-[var(--bg-soft)] block">
                              <img src={url} alt={`Produit ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">Description</p>
                        <p className="font-medium mt-0.5 line-clamp-2">{s.description || "—"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">Poids</p>
                        <p className="font-medium mt-0.5">{s.weight_kg ? `${s.weight_kg} kg` : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">Volume</p>
                        <p className="font-medium mt-0.5">{s.volume_cbm ? `${s.volume_cbm} m³` : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)]">Livraison estimée</p>
                        <p className="font-medium mt-0.5">{s.estimated_delivery ? formatDate(s.estimated_delivery) : "—"}</p>
                      </div>
                    </div>

                    <div>
                      <p className="kicker">Historique</p>
                      <ol className="mt-3 relative border-l-2 border-[var(--accent)]/20 ml-2 space-y-3">
                        {[...(s.events || [])].reverse().map((ev, i) => {
                          const evSt = STATUS_LABELS[ev.status] || { label: ev.status, color: "bg-black/60" };
                          return (
                            <li key={ev.id || i} className="pl-5 relative">
                              <span className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full ${i === 0 ? "bg-[var(--accent)] ring-4 ring-[var(--accent)]/20" : "bg-[var(--ink-soft)]/50"}`} />
                              <div className="flex flex-wrap items-baseline gap-2">
                                <span className="text-sm font-bold">{evSt.label}</span>
                                <span className="text-xs text-[var(--ink-soft)]">{formatDate(ev.at)}</span>
                              </div>
                              {ev.location && <p className="text-xs text-[var(--ink-soft)] mt-0.5">📍 {ev.location}</p>}
                              {ev.note && <p className="text-xs mt-0.5">{ev.note}</p>}
                            </li>
                          );
                        })}
                      </ol>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Link
                        to={`/suivi?n=${s.tracking_number}`}
                        className="btn-outline text-xs !py-1.5 !px-3 inline-flex"
                        data-testid={`open-tracking-${s.tracking_number}`}
                      >
                        Voir le suivi public
                      </Link>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/shipments/${s.tracking_number}/receipt.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline text-xs !py-1.5 !px-3 inline-flex"
                        data-testid={`open-pdf-${s.tracking_number}`}
                      >
                        📄 Bon d'expédition (PDF)
                      </a>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
